import { useState, useEffect } from 'react';
import useScrollReveal from '../useScrollReveal';
import { sectionStyles, isColorDark, getAnimation, resolveLinkProps, hexToRgba, resolveVideo, buildSrcSet } from '../utils';
import { getButtonIcon, resolveIconKey } from '../buttonIcons';
import SectionDivider from './SectionDivider';

export default function PromoSection({ block = {}, primaryColor, enableAnimations, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const accent = config.accent_color || primaryColor;
    const bgColor = config.bg_color || accent;
    const dark = isColorDark(bgColor);
    const textColor = config.text_color || (dark ? '#ffffff' : '#111827');
    const textSub = dark ? 'rgba(255,255,255,0.75)' : '#6b7280';
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const variant = config.variant || 'banner';
    const [showVideoModal, setShowVideoModal] = useState(false);
    const [carouselIdx, setCarouselIdx] = useState(0);
    const mediaType = config.media_type || 'image';
    const folderImages = content.folder_id ? (mediaFolders[content.folder_id] || []) : [];
    const _selPromo = content.selected_file_ids;
    const selectedImages = _selPromo?.length > 0 ? folderImages.filter(f => _selPromo.includes(f.id)) : folderImages;

    useEffect(() => {
        if (variant !== 'with-image' || mediaType !== 'carousel' || selectedImages.length <= 1) return;
        const t = setInterval(() => setCarouselIdx(p => (p + 1) % selectedImages.length), 4000);
        return () => clearInterval(t);
    }, [variant, mediaType, selectedImages.length]);

    const sectionButtons = content.buttons && content.buttons.length > 0
        ? content.buttons
        : content.cta_text ? [{ text: content.cta_text, link: content.cta_link || '#contact', style: 'solid', icon: '', bg_color: '', text_color: '' }] : [];

    // ── Floating card ──
    if (variant === 'floating') {
        return (
            <section className="py-10 md:py-16 px-4 md:px-8">
                <div ref={ref} className={`max-w-4xl mx-auto rounded-3xl overflow-hidden transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                    <div className="relative p-8 md:p-12" style={{ backgroundColor: bgColor }}>
                        <div className="absolute top-0 right-0 w-40 h-40 rounded-full bg-white/5 -translate-y-1/2 translate-x-1/2"/>
                        <div className="relative z-10 flex flex-col md:flex-row items-center gap-6">
                            <div className="flex-1">
                                {content.badge && <span className="inline-block px-3 py-1 rounded-full text-xs font-bold mb-3" style={{ backgroundColor: 'rgba(255,255,255,0.2)', color: textColor }}>{content.badge}</span>}
                                <h3 className="text-2xl md:text-3xl font-bold" style={{ color: textColor }}>{content.title || 'Promocion'}</h3>
                                {content.subtitle && <p className="text-base mt-2" style={{ color: textSub }}>{content.subtitle}</p>}
                            </div>
                            {sectionButtons.map((btn, i) => {
                                const btnStyle = btn.style || 'solid';
                                const icon = btn.icon && getButtonIcon(resolveIconKey(btn.icon));
                                const lp = resolveLinkProps(btn.link || '#');
                                const customBg = btn.bg_color;
                                const customText = btn.text_color;
                                let cls = 'shrink-0 px-8 py-3.5 rounded-full font-bold text-sm transition-all hover:scale-105 inline-flex items-center gap-2 ';
                                let style = {};
                                if (btnStyle === 'solid' || btnStyle === 'pill') {
                                    if (customBg) { style.backgroundColor = customBg; style.color = customText || (isColorDark(customBg) ? '#fff' : '#111'); cls += 'shadow-lg hover:shadow-xl'; }
                                    else { style.backgroundColor = dark ? '#ffffff' : '#111827'; style.color = dark ? bgColor : '#ffffff'; cls += 'shadow-lg hover:shadow-xl'; }
                                } else if (btnStyle === 'outline') {
                                    if (customBg) { style.color = customBg; style.borderColor = customBg; cls += 'border-2'; }
                                    else { style.color = dark ? '#ffffff' : '#111827'; style.borderColor = dark ? '#ffffff' : '#111827'; cls += 'border-2 hover:opacity-80'; }
                                } else { style.color = customText || (dark ? '#ffffff' : '#111827'); cls += 'hover:opacity-80'; }
                                if (customText && (btnStyle === 'solid' || btnStyle === 'pill')) style.color = customText;
                                return <a key={i} {...lp} className={cls} style={style}>{icon}{btn.text}</a>;
                            })}
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    // ── With image ──
    if (variant === 'with-image') {
        const img = selectedImages[0];
        const video = resolveVideo(content, 'video', mediaFolders);
        const videoThumb = (() => {
            if (content.video_thumb_folder_id && content.video_thumb_file_id && mediaFolders[content.video_thumb_folder_id]) {
                const f = mediaFolders[content.video_thumb_folder_id].find(x => String(x.id) === String(content.video_thumb_file_id));
                if (f) return f.image_url;
            }
            return content.video_thumb_url || video?.thumb || null;
        })();
        const MediaPanel = () => {
            if (mediaType === 'video' && video) {
                if (video.type === 'file') return (
                    <video src={video.src} className="w-full h-full object-cover" controls preload="metadata" />
                );
                return (
                <>
                    <div className="relative w-full h-full cursor-pointer group" onClick={() => setShowVideoModal(true)}>
                        {videoThumb && <img src={videoThumb} alt="" decoding="async" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                        {!videoThumb && <div className="absolute inset-0 bg-white/10" />}
                        <div className="absolute inset-0 bg-black/10 group-hover:bg-black/30 transition-colors duration-300" />
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-16 h-16 rounded-full flex items-center justify-center text-white transition-all duration-300 group-hover:scale-110"
                                style={{ backgroundColor: hexToRgba(accent, 0.85), boxShadow: `0 0 0 8px ${hexToRgba(accent, 0.15)}` }}>
                                <svg className="w-7 h-7 ml-1" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                            </div>
                        </div>
                    </div>
                    {showVideoModal && (
                        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 p-4 md:p-8" onClick={() => setShowVideoModal(false)}>
                            <div className="relative w-full max-w-5xl" onClick={e => e.stopPropagation()}>
                                <button onClick={() => setShowVideoModal(false)} className="absolute -top-12 right-0 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors z-10">
                                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
                                </button>
                                <div className="relative rounded-2xl overflow-hidden shadow-2xl aspect-video">
                                    {video.type === 'file'
                                        ? <video src={video.src} className="absolute inset-0 w-full h-full" controls autoPlay />
                                        : <iframe src={`${video.embed}?autoplay=1&rel=0`} className="absolute inset-0 w-full h-full" style={{ border: 'none' }}
                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />}
                                </div>
                            </div>
                        </div>
                    )}
                </>
                );
            }
            if (mediaType === 'carousel' && selectedImages.length > 1) return (
                <div className="relative w-full h-full">
                    {selectedImages.map((im, i) => (
                        <img key={im.id} src={im.image_url} alt={im.title || ''} loading="lazy" decoding="async"
                            srcSet={buildSrcSet(im)} sizes="(max-width:1024px) 100vw, 50vw"
                            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${i === carouselIdx % selectedImages.length ? 'opacity-100' : 'opacity-0'}`} />
                    ))}
                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
                        {selectedImages.map((_, i) => (
                            <button key={i} onClick={() => setCarouselIdx(i)}
                                className={`w-2 h-2 rounded-full transition-all ${i === carouselIdx % selectedImages.length ? 'bg-white scale-125' : 'bg-white/50'}`} />
                        ))}
                    </div>
                </div>
            );
            if (img?.image_url) return <img src={img.image_url} alt="" decoding="async" srcSet={buildSrcSet(img)} sizes="(max-width:1024px) 100vw, 50vw" className="w-full h-full object-cover" />;
            return <div className="w-full h-full flex items-center justify-center bg-white/10"><span className="text-6xl opacity-30">🎓</span></div>;
        };

        return (
            <section className="py-10 md:py-16 px-4 md:px-8">
                <div ref={ref} className={`max-w-5xl mx-auto rounded-3xl overflow-hidden shadow-xl transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                    style={{ backgroundColor: bgColor }}>
                    <div className="grid grid-cols-1 md:grid-cols-2">
                        <div className="p-8 md:p-12 flex flex-col justify-center">
                            {content.badge && <span className="inline-block px-3 py-1 rounded-full text-xs font-bold mb-3 w-fit" style={{ backgroundColor: 'rgba(255,255,255,0.2)', color: textColor }}>{content.badge}</span>}
                            <h3 className="text-2xl md:text-3xl font-bold" style={{ color: textColor }}>{content.title || 'Promocion'}</h3>
                            {content.subtitle && <p className="text-base mt-3" style={{ color: textSub }}>{content.subtitle}</p>}
                            {sectionButtons.length > 0 && (
                                <div className="flex flex-wrap gap-3 mt-6">
                                    {sectionButtons.map((btn, i) => {
                                        const btnStyle = btn.style || 'solid';
                                        const icon = btn.icon && getButtonIcon(resolveIconKey(btn.icon));
                                        const lp = resolveLinkProps(btn.link || '#');
                                        const customBg = btn.bg_color;
                                        const customText = btn.text_color;
                                        let cls = 'px-8 py-3 rounded-full font-bold text-sm transition-all hover:scale-105 inline-flex items-center gap-2 ';
                                        let style = {};
                                        if (btnStyle === 'solid' || btnStyle === 'pill') {
                                            if (customBg) { style.backgroundColor = customBg; style.color = customText || (isColorDark(customBg) ? '#fff' : '#111'); cls += 'shadow-lg hover:shadow-xl'; }
                                            else { style.backgroundColor = dark ? '#ffffff' : '#111827'; style.color = dark ? bgColor : '#ffffff'; cls += 'shadow-lg'; }
                                        } else if (btnStyle === 'outline') {
                                            if (customBg) { style.color = customBg; style.borderColor = customBg; cls += 'border-2'; }
                                            else { style.color = dark ? '#ffffff' : '#111827'; style.borderColor = dark ? '#ffffff' : '#111827'; cls += 'border-2 hover:opacity-80'; }
                                        } else { style.color = customText || (dark ? '#ffffff' : '#111827'); cls += 'hover:opacity-80'; }
                                        if (customText && (btnStyle === 'solid' || btnStyle === 'pill')) style.color = customText;
                                        return <a key={i} {...lp} className={cls} style={style}>{icon}{btn.text}</a>;
                                    })}
                                </div>
                            )}
                        </div>
                        <div className="aspect-square md:aspect-[4/3] relative overflow-hidden">
                            <MediaPanel />
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    // ── Banner (default) ──
    return (
        <section ref={ref} className="relative overflow-hidden" style={{ backgroundColor: bgColor }}>
            <div className="absolute top-0 left-0 w-48 h-48 rounded-full bg-white/5 -translate-x-1/3 -translate-y-1/3"/>
            <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-4 transition-all duration-700 ${visible ? 'opacity-100' : 'opacity-0'}`}>
                <div className="flex items-center gap-4 flex-wrap">
                    {content.badge && <span className="px-3 py-1 rounded-full text-xs font-bold" style={{ backgroundColor: 'rgba(255,255,255,0.2)', color: textColor }}>{content.badge}</span>}
                    <h3 className="text-base md:text-lg font-bold" style={{ color: textColor }}>{content.title || 'Promocion'}</h3>
                    {content.subtitle && <span className="text-sm" style={{ color: textSub }}>— {content.subtitle}</span>}
                </div>
                {sectionButtons.map((btn, i) => {
                    const btnStyle = btn.style || 'solid';
                    const icon = btn.icon && getButtonIcon(resolveIconKey(btn.icon));
                    const lp = resolveLinkProps(btn.link || '#');
                    const customBg = btn.bg_color;
                    const customText = btn.text_color;
                    let cls = 'shrink-0 px-6 py-2.5 rounded-full font-bold text-sm transition-all hover:scale-105 inline-flex items-center gap-2 ';
                    let style = {};
                    if (btnStyle === 'solid' || btnStyle === 'pill') {
                        if (customBg) { style.backgroundColor = customBg; style.color = customText || (isColorDark(customBg) ? '#fff' : '#111'); }
                        else { style.backgroundColor = dark ? '#ffffff' : '#111827'; style.color = dark ? bgColor : '#ffffff'; }
                    } else if (btnStyle === 'outline') {
                        if (customBg) { style.color = customBg; style.borderColor = customBg; cls += 'border-2'; }
                        else { style.color = dark ? '#ffffff' : '#111827'; style.borderColor = dark ? '#ffffff' : '#111827'; cls += 'border-2 hover:opacity-80'; }
                    } else { style.color = customText || (dark ? '#ffffff' : '#111827'); cls += 'hover:opacity-80'; }
                    if (customText && (btnStyle === 'solid' || btnStyle === 'pill')) style.color = customText;
                    return <a key={i} {...lp} className={cls} style={style}>{icon}{btn.text}</a>;
                })}
            </div>
        </section>
    );
}
